import type { FieldErrors } from '../api/errors';
import { systemFieldRequirement } from '../domain/validation';
import { selectFrequencies, selectHeading, selectParagraph, shouldShowError } from '../state/selectors';
import { useEnv, useFormState, useMoney } from './context';
import { Field } from './fields/Field';
import { discountLabel } from './format';
import { Section } from './Section';

interface Props {
  errors: FieldErrors;
}

export function FrequencyPicker({ errors }: Props) {
  const { controller } = useEnv();
  const money = useMoney();
  const state = useFormState();
  const frequencies = selectFrequencies(state).filter((f) => typeof f.id === 'number');
  if (!frequencies.length) return null;
  const field = systemFieldRequirement(state.bootstrap?.form ?? null, 'frequency', { requireZip: false });
  const selected = frequencies.find((f) => f.id === state.values.frequencyId) ?? null;
  const excludeFirst = selected && (selected.interval ?? 'o') !== 'o' && selected.exclude_first === true;
  const show = shouldShowError(state, 'frequency_id', 2) ? Array.from(new Set([...(errors['frequency_id'] ?? []), ...(state.apiErrors['frequency_id'] ?? [])])) : [];

  return (
    <Section heading={selectHeading(state, 'frequency_section')} paragraph={selectParagraph(state, 'schedule_information')}>
      <Field fieldKey="frequency_id" label={field.label} required errors={show} help={excludeFirst ? 'Recurring discounts apply from your second visit.' : null} group>
        {() => (
          <div className="cbf-choice-grid cbf-choice-grid-2" role="radiogroup" aria-label={field.label}>
            {frequencies.map((f) => {
              const on = f.id === state.values.frequencyId;
              const save = discountLabel(f.percent, f.amount, money);
              const id = `cbf-frequency-${f.id}`;
              return (
                <label key={f.id} className={`cbf-choice${on ? ' is-on' : ''}`} htmlFor={id}>
                  <input id={id} type="radio" name="cbf-frequency" checked={on} onChange={() => controller.send({ type: 'FREQUENCY_SELECTED', frequencyId: f.id! })} />
                  <span className="cbf-radio" aria-hidden="true" />
                  <span className="cbf-choice-body">
                    <span className="cbf-choice-title">
                      <span>{f.name}</span>
                      {save ? <span className="cbf-badge">{save}</span> : null}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </Field>
    </Section>
  );
}
